import { CloseIcon, } from '@chakra-ui/icons';
import { Divider, Flex, Text, useToast } from '@chakra-ui/react';
import { AntiVirusStatuses } from 'common';
import MyModal from 'components/MyModal';
import AdditionalData from 'containers/AdditionalData';
import UploadFileInput from 'containers/UploadFileInput';
import useUploadPrefund from 'hooks/useUploadPrefund';
import { DisbursementDataType, PaymentKey } from 'pages/Payment';
import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApproverCreateProcessesMutation } from 'redux/services/approvalProcessor';
import { ApproverCreateProcessesRequest } from 'redux/services/approvalProcessor/types';
import { useGetProfileQuery } from 'redux/services/membership';
import { getOrganisationMembership } from 'utils';

interface UploadPrefundModalProps {
    readonly isOpen: boolean;
    readonly setOpenModalCreate: () => void;
    readonly processId: string;
    readonly orgId?: string;
    onCloseComplete?: () => void;
}

const UploadPrefundModal = (
    { isOpen, setOpenModalCreate, onCloseComplete, processId, orgId }: UploadPrefundModalProps
) => {
    const [paymentProofFiles, setPaymentProofFiles] = useState(undefined);

    const intervalPrefundRef: React.MutableRefObject<any> = useRef(null);

    const onDropProof = useCallback((acceptedFiles) => {
        setPaymentProofFiles(acceptedFiles);
    }, []);

    const {
        getRootProps: getProofRootProps,
        getInputProps: getProofInputProps,
        open: openProofInputProps,
        isDragActive: isProofDragActive,
    } = useDropzone({
        onDrop: onDropProof,
        multiple: false,
        accept: [".jpeg", ".jpg", ".png", ".pdf"],
        disabled: false,
    });

    const toast = useToast();

    const { data: profileData } = useGetProfileQuery();
    const membershipOrganisation = getOrganisationMembership(
        profileData?.data?.memberships
    );

    const [
        approverCreateProcesses,
        {
            isLoading: isCreateApprovalProcessLoading,
        },
    ] = useApproverCreateProcessesMutation();

    const defaultDisbursementData: DisbursementDataType =
    {
        key: PaymentKey.UPLOAD_PROOF_PREFUND,
        title: "Upload proof of prefund",
        supportText:
            "Max file size is 5MB. Supported file types are .jpg, .png and .pdf.",
        validations: {
            maxFileSize: {
                value: 5 * 1024 * 1024,
                message: "File exceeds size limit",
            },
            fileType: {
                value: [".jpeg", ".jpg", ".png", ".pdf"],
                message: "File type is invalid",
            },
        },
        isSuccess: false,
        rootProps: getProofRootProps,
        inputProps: getProofInputProps,
        open: openProofInputProps,
        files: [],
    };

    const {
        isLoadingPrefund,
        proofPrefundFile,
        disbursementData: hookDisbursementData,
        prefundDocumentDetail,
        hasErrorPrefund,
        errorMessagesPrefund,
        uploadProofFileData,
        isEnableSubmit,
        handleRemoveFile
    } = useUploadPrefund({ defaultDisbursementData: [defaultDisbursementData], paymentProofFiles, intervalPrefundRef });

    const disbursementData = hookDisbursementData[0] ?? defaultDisbursementData;

    const handleSubmit = (): void => {
        const body: ApproverCreateProcessesRequest = {
            orgToken: membershipOrganisation?.token,
            processId,
            processVariables: {
                overallInformation: {
                    relatedDocumentId: uploadProofFileData?.data?.id,
                    relatedDocumentName: proofPrefundFile?.fileName || "",
                },
            },
        };
        approverCreateProcesses(body).unwrap()
            .then(() => {
                toast({
                    title: "File Submitted Successfully",
                    description: "",
                    status: "success",
                    duration: 9000,
                    position: "top-right",
                    isClosable: true,
                });
                setOpenModalCreate();
                onCloseComplete?.();
            }).catch((error) => {
                const errorMessage = error?.data?.message || "An error occurred while submitting the file.";
                toast({
                    title: "Error",
                    description: errorMessage,
                    status: "error",
                    duration: 9000,
                    position: "top-right",
                    isClosable: true,
                });
            });
    };

    const isHasFiles = disbursementData?.files && disbursementData?.files?.length > 0;

    return (
        <MyModal
            isOpen={isOpen}
            onClose={() => setOpenModalCreate()}
            title="Upload proof of prefund"
            submitBtnText="Submit"
            cancelBtnText="Cancel"
            onSubmit={handleSubmit}
            loadingSubmit={isEnableSubmit && isCreateApprovalProcessLoading}
            scrollBehavior="inside"
            disabledCloseOutSide
            disableSubmit={!isEnableSubmit}
            closeable={false}
        >
            {!isHasFiles && (
                <UploadFileInput
                    disbursementData={disbursementData}
                    isHasFiles={isHasFiles}
                    isDragActive={isProofDragActive}
                />
            )}

            {disbursementData?.files?.map((file) => (
                <Flex
                    key={file.fileName}
                    flexDir="column"
                    border={
                        file?.errors ||
                            (disbursementData.key === PaymentKey.UPLOAD_PROOF_PREFUND &&
                                prefundDocumentDetail?.data?.antiVirusStatus ===
                                AntiVirusStatuses.INFECTED)
                            ? "1px solid #DA1E28"
                            : ""
                    }
                    mt={"10px"}
                >
                    <Flex
                        bgColor={"#F4F4F4"}
                        height="48px"
                        alignItems="center"
                        justifyContent="space-between"
                        px="16px"
                        data-testid={`${disbursementData.key}-filename-uploaded`}
                    >
                        <Text fontSize={"10px"} noOfLines={1}>
                            {file.fileName}
                        </Text>
                        <CloseIcon
                            data-testid={`${disbursementData.key}-close-icon`}
                            cursor="pointer"
                            onClick={() => {
                                if (isCreateApprovalProcessLoading) return;

                                handleRemoveFile(
                                    disbursementData.key,
                                    intervalPrefundRef,
                                );
                            }}
                        />
                    </Flex>
                    <Divider
                        h="1px"
                        w="100%"
                        bgColor="#E7E7E7"
                        borderColor={"initial"}
                        display={!file?.errors ? "none" : "block"}
                    />

                    {disbursementData?.key === PaymentKey.UPLOAD_PROOF_PREFUND &&
                        (!file?.errors || file?.errors?.length === 0) &&
                        <AdditionalData
                            isLoading={isLoadingPrefund}
                            hasError={hasErrorPrefund}
                            errorMessages={errorMessagesPrefund}
                            doc={prefundDocumentDetail}
                        />}

                    {file?.errors?.map((error) => (
                        <Flex
                            key={error}
                            bgColor={"#F4F4F4"}
                            alignItems="center"
                            data-testid={`${disbursementData.key}-error-messages`}
                            w="100%"
                        >
                            <Text
                                color={"red"}
                                px="16px"
                                py="8px"
                                whiteSpace="pre-wrap"
                                maxW="100%"
                                wordBreak="break-word"
                            >
                                {error}
                            </Text>
                        </Flex>
                    ))}
                </Flex>
            ))}
        </MyModal>
    );
};

export default UploadPrefundModal;
